/**
 * A client's zone outcomes (`client.zones`): what the SDK did with the zones
 * its views loaded, as typed records a host reads instead of trace names.
 *
 * @module
 */
import type { Option, Unsubscribe } from "@tento-lona/sheets";
import { TimezoneRegion, Tzname } from "@tento-chrono";
import type { TimezonePreferences } from "../preferences/timezone-preferences.ts";

/**
 * The zone outcomes of one client (`client.zones`), for a host to tell the
 * viewer what it tolerates: the viewer's own fallback
 * (`TimezonePreferences.peekZoneFallback`); every other fallback a zone read
 * took, with the read that asked (`source`: an event's zone, an iCal `TZID`,
 * a recurrence rule's zone, a ground index, a flight, the home zone); the
 * zones whose times come from the platform's offsets (no served data, or
 * served data the platform disagreed with); and a zone data version the
 * service does not serve. `take()` drains what arrived since the last take;
 * `subscribe` hears each new outcome as it arrives.
 *
 * The record is the client's own: one client's `take` never drains
 * another's, and it records from the client's construction until `dispose`.
 * Zone data itself is the process's (chrono loads and caches each zone once
 * per process, `TimezoneRegion.onOutcome`), so every open client hears a
 * fallback or a platform-built zone whichever caller loaded it, and a zone
 * built before the client opened is recorded when one of its views loads it.
 * The `Tracing` entries (`tz/zone-fallback`, `zone/unserved`,
 * `zone/offset-mismatch`, `tz/data-version-fallback`) stay as telemetry.
 */
export class ZoneOutcomes {
  readonly #preferences: TimezonePreferences;
  readonly #listenerErrors: ZoneOutcomes.ErrorSink;
  readonly #fallbacks = new Map<string, ZoneOutcomes.Fallback>();
  readonly #fromPlatform = new Map<string, ZoneOutcomes.FromPlatform>();
  readonly #subscribers = new Set<(outcome: ZoneOutcomes.Outcome) => void>();

  /**
   * Record `preferences`' client's outcomes until `owner` is disposed (the
   * client's lifecycle); a subscriber's throw goes to `listenerErrors` (the
   * client's `listenerErrorHandler`).
   */
  constructor(preferences: TimezonePreferences, owner: ZoneOutcomes.Owner, listenerErrors: ZoneOutcomes.ErrorSink) {
    this.#preferences = preferences;
    this.#listenerErrors = listenerErrors;
    const stop = TimezoneRegion.onOutcome((outcome) => this.#hear(outcome));
    preferences.liveZoneFallback.addListener(this, (fallback) => this.#tell({ kind: "viewer", fallback }));
    owner.onDisposed(() => {
      stop();
      preferences.liveZoneFallback.removeListener(this);
      this.#subscribers.clear();
    });
  }

  /**
   * The outcomes since the last `take`, each fallback (by source, name and
   * zone used) and each platform-built zone once, sorted by name; then
   * forgotten. The viewer's fallback and the data version fallback are
   * standing conditions: each take reads them as they are now.
   */
  take(): ZoneOutcomes.Taken {
    const taken: ZoneOutcomes.Taken = {
      viewer: this.#preferences.peekZoneFallback(),
      fallbacks: [...this.#fallbacks.values()].sort((a, b) =>
        a.requested.localeCompare(b.requested) || a.used.localeCompare(b.used) || a.source.localeCompare(b.source)
      ),
      fromPlatform: [...this.#fromPlatform.values()].sort((a, b) => a.zone.localeCompare(b.zone)),
      dataVersion: TimezoneRegion.dataVersionFallback() ?? null,
    };
    this.#fallbacks.clear();
    this.#fromPlatform.clear();
    return taken;
  }

  /**
   * Tell `listener` each outcome as it reaches the record: a fallback or a
   * platform-built zone not already in it since the last `take`, a change
   * of the viewer's fallback, a data version fallback. A throw goes to the
   * client's `listenerErrorHandler` after every subscriber heard it.
   */
  subscribe(listener: (outcome: ZoneOutcomes.Outcome) => void): Unsubscribe {
    this.#subscribers.add(listener);
    return () => {
      this.#subscribers.delete(listener);
    };
  }

  #hear(outcome: TimezoneRegion.Outcome): void {
    switch (outcome.kind) {
      case "fallback": {
        const fallback: ZoneOutcomes.Fallback = {
          requested: outcome.requested,
          used: String(outcome.used),
          source: sourceOf(outcome.source),
          reason: Tzname.parseOpt(outcome.requested) == null ? "unknown-name" : "not-loaded",
        };
        const key = `${fallback.source}\n${fallback.requested}\n${fallback.used}`;
        if (this.#fallbacks.has(key)) return;
        this.#fallbacks.set(key, fallback);
        this.#tell({ kind: "fallback", fallback });
        return;
      }
      case "from-platform": {
        const zone = fromPlatformOf(outcome.region);
        if (zone == null || this.#fromPlatform.has(zone.zone)) return;
        this.#fromPlatform.set(zone.zone, zone);
        this.#tell({ kind: "from-platform", zone });
        return;
      }
      case "data-version-fallback":
        this.#tell({ kind: "data-version", fallback: { asked: outcome.asked, used: outcome.used } });
        return;
    }
  }

  #tell(outcome: ZoneOutcomes.Outcome): void {
    const thrown: unknown[] = [];
    for (const listener of [...this.#subscribers]) {
      try {
        listener(outcome);
      } catch (e) {
        thrown.push(e);
      }
    }
    if (thrown.length === 1) this.#listenerErrors(thrown[0]);
    else if (thrown.length > 1) this.#listenerErrors(new AggregateError(thrown, "ZoneOutcomes: subscribers threw"));
  }
}

/** A chrono or `ZoneLoad` source name (`ZoneLoad.Source`, the same names) as a `ZoneOutcomes.Source` (a name neither knows: `other`). */
function sourceOf(source: string): ZoneOutcomes.Source {
  switch (source) {
    case "event":
    case "ical-tzid":
    case "rrule":
    case "ground-index":
    case "flight":
    case "home-zone":
    case "runtime":
      return source;
    default:
      return "other";
  }
}

function fromPlatformOf(region: TimezoneRegion): Option<ZoneOutcomes.FromPlatform> {
  const zone = String(region.fullname);
  if (region.unserved != null) return { zone, cause: "unserved", reason: region.unserved };
  if (region.mismatch == null) return null;
  const { at, servedMins, platformMins } = region.mismatch;
  return { zone, cause: "offset-mismatch", at: new Date(at).toISOString(), servedMins, platformMins };
}

/** Types that go with `ZoneOutcomes`. */
export namespace ZoneOutcomes {
  /** What the record lives as long as: the client's lifecycle (`LonaSdk.Client.Lifecycle`). */
  export interface Owner {
    /** Run `watcher` once when the owner is disposed; the returned function cancels it. */
    onDisposed(watcher: () => void): Unsubscribe;
  }

  /** Where a subscriber's throw goes: the client's `ListenerErrorHandler`. */
  export type ErrorSink = (error: unknown) => void;

  /** What `take()` returns. */
  export interface Taken {
    /** The viewer's fallback while it stands (`TimezonePreferences.peekZoneFallback`); `null`: the viewer's zone loaded. */
    readonly viewer: Option<TimezonePreferences.ZoneFallback>;
    /** Every other zone read that fell back since the last take, each once. */
    readonly fallbacks: readonly Fallback[];
    /** The zones loaded since the last take whose times come from the platform's offsets, each once. */
    readonly fromPlatform: readonly FromPlatform[];
    /** The zone data version the loader reads instead of the one it asked for, while it does (`null`: none). */
    readonly dataVersion: Option<DataVersionFallback>;
  }

  /**
   * The read a fallback came from: an event's zone (`event`), an iCal
   * line's TZID (`ical-tzid`), a recurrence rule's zone (`rrule`), a ground
   * index that names no zone (`ground-index`), a flight's zone (`flight`),
   * the home transition's zone (`home-zone`), the runtime's zone
   * (`runtime`), or another caller (`other`).
   */
  export type Source = "event" | "ical-tzid" | "rrule" | "ground-index" | "flight" | "home-zone" | "runtime" | "other";

  /** A zone read that did not load `requested` and read `used` instead (UTC, mostly), from `source`. */
  export interface Fallback {
    /** The zone name the read asked for. */
    readonly requested: string;
    /** The zone it read instead. */
    readonly used: string;
    /** The read that asked. */
    readonly source: Source;
    /** `unknown-name`: the runtime knows no zone by that name; `not-loaded`: the zone did not load. */
    readonly reason: TimezonePreferences.ZoneFallback.Reason;
  }

  /**
   * Why a zone has no served data: the service has no file (`not-found`) or
   * a file with no transitions (`no-transitions`) for it, or it gave no
   * answer (`no-answer`: a failed fetch, the deadline, a 5xx, 429 or 408;
   * held for `TimezoneRegion.UNLOADABLE_RETRY_MS`, then asked again in the
   * background until served data replaces it).
   */
  export type UnservedReason = "not-found" | "no-transitions" | "no-answer";

  /** A zone whose times come from the platform's offsets: no served data (`unserved`), or served data the platform disagreed with (`offset-mismatch`). */
  export type FromPlatform = Unserved | OffsetMismatch;

  /** A zone the service has no data for, or did not answer for, built from the platform's offsets. */
  export interface Unserved {
    /** The zone's name. */
    readonly zone: string;
    /** Always `unserved`. */
    readonly cause: "unserved";
    /** Why it has no served data. */
    readonly reason: UnservedReason;
  }

  /** A served zone whose offsets the platform disagreed with, rebuilt from the platform's. */
  export interface OffsetMismatch {
    /** The zone's name. */
    readonly zone: string;
    /** Always `offset-mismatch`. */
    readonly cause: "offset-mismatch";
    /** The first instant checked where they disagreed (ISO 8601). */
    readonly at: string;
    /** The served offset there, in minutes east of UTC. */
    readonly servedMins: number;
    /** The platform's offset there, in minutes east of UTC. */
    readonly platformMins: number;
  }

  /** A zone data version the service does not serve (`asked`), and the one the loader reads instead (`used`). */
  export interface DataVersionFallback {
    /** The data version the loader asked for. */
    readonly asked: string;
    /** The data version it reads instead. */
    readonly used: string;
  }

  /** One outcome as it reaches the record (`subscribe`): its `kind`, and the record it carries. */
  export type Outcome =
    | { readonly kind: "viewer"; readonly fallback: Option<TimezonePreferences.ZoneFallback> }
    | { readonly kind: "fallback"; readonly fallback: Fallback }
    | { readonly kind: "from-platform"; readonly zone: FromPlatform }
    | { readonly kind: "data-version"; readonly fallback: DataVersionFallback };
}
